import React, { useContext, useState } from 'react'
import { Disclosure, Transition } from '@headlessui/react'
import { CogIcon, ChevronDownIcon } from '@heroicons/react/outline'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { configurationSubItems } from './sidebarNavigation'
import { userHasPermission } from '../../navigation/NavegationRoutes'
import { UserContext } from '../../hooks/UserContext'
import { BetaBadge } from '../BetaBadge'

export default function SidebarConfigSection({ warehouseId, isCollapsed, onExpand }) {
    const { t } = useTranslation()
    const location = useLocation()
    const context = useContext(UserContext)
    const user = context?.user

    const [forceOpen, setForceOpen] = useState(false)

    if (!user) return null

    const visibleItems = configurationSubItems.filter((item) => {
        if (item.hiddenFromSidebar) return false
        return item.canView ? item.canView(user) : userHasPermission(user, item.permission_key)
    })

    if (visibleItems.length === 0) return null

    const anyActive = visibleItems.some((item) => {
        const resolved = item.href.replace(':warehouse_id', warehouseId)
        return location.pathname === resolved || location.pathname.startsWith(resolved + '/')
    })

    if (isCollapsed) {
        return (
            <div className="mt-2 pt-2 border-t flex justify-center" style={{ borderColor: '#ebebeb' }}>
                <button
                    onClick={() => { setForceOpen(true); onExpand(); }}
                    title={t('navegation_routes.configurations')}
                    aria-label={t('navegation_routes.configurations')}
                    className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-gray-100"
                >
                    <CogIcon className="w-5 h-5" style={{ color: '#DCA118' }} />
                </button>
            </div>
        )
    }

    return (
        <div className="mt-2 pt-2 border-t" style={{ borderColor: '#ebebeb' }}>
            <Disclosure defaultOpen={anyActive || forceOpen}>
                {({ open }) => (
                    <>
                        <Disclosure.Button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900">
                            <CogIcon className="w-5 h-5 flex-shrink-0" style={{ color: '#DCA118' }} />
                            <span className="truncate">{t('navegation_routes.configurations')}</span>
                            <ChevronDownIcon
                                className={`w-3 h-3 ml-auto flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                                style={{ color: '#DCA118' }}
                            />
                        </Disclosure.Button>
                        <Transition
                            enter="transition-all duration-200 ease-out"
                            enterFrom="max-h-0 opacity-0"
                            enterTo="max-h-96 opacity-100"
                            leave="transition-all duration-150 ease-in"
                            leaveFrom="max-h-96 opacity-100"
                            leaveTo="max-h-0 opacity-0"
                        >
                            <Disclosure.Panel static className="pl-5 overflow-hidden">
                                {visibleItems.map((item) => {
                                    const resolved = item.href.replace(':warehouse_id', warehouseId)
                                    const isActive = location.pathname === resolved ||
                                        location.pathname.startsWith(resolved + '/')
                                    const label = item.name.startsWith('configurations.')
                                        ? t(item.name)
                                        : t(`navegation_routes.${item.name}`)
                                    return (
                                        <Link
                                            key={item.id}
                                            to={resolved}
                                            className={`flex items-center gap-2 px-3 py-1.5 text-xs rounded-md
                                                ${isActive
                                                    ? 'bg-gray-100 text-gray-900 font-medium'
                                                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                                                }`}
                                        >
                                            <span>{label}</span>
                                            {item.permission_key === 'beta_features' && <BetaBadge />}
                                        </Link>
                                    )
                                })}
                            </Disclosure.Panel>
                        </Transition>
                    </>
                )}
            </Disclosure>
        </div>
    )
}
